import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Mail, 
  Lock, 
  User, 
  AlertCircle, 
  Sparkles,
  TrendingUp,
  Users,
  Calendar,
  ChevronRight,
  Loader2
} from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';

// Floating particle component
const FloatingParticle = ({ delay, duration, size, left, top }) => (
  <div
    className="absolute rounded-full bg-[#4b95d1]/20 animate-pulse"
    style={{
      width: size,
      height: size,
      left: `${left}%`,
      top: `${top}%`,
      animationDelay: `${delay}s`,
      animationDuration: `${duration}s`,
    }}
  />
);

// Animated feature card
const FeatureCard = ({ icon: Icon, title, description, delay }) => (
  <div 
    className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 transform hover:scale-105 transition-all duration-300 opacity-0 animate-fade-in-up"
    style={{ animationDelay: `${delay}ms`, animationFillMode: 'forwards' }}
  >
    <div className="flex items-center gap-3">
      <div className="bg-[#4b95d1]/20 p-2 rounded-lg">
        <Icon className="w-5 h-5 text-[#4b95d1]" />
      </div>
      <div>
        <h3 className="text-white font-medium text-sm">{title}</h3>
        <p className="text-white/60 text-xs">{description}</p>
      </div>
    </div>
  </div>
);

const LoginPage = () => {
  const { login, register } = useAuth();
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [activeTab, setActiveTab] = useState('login');

  useEffect(() => {
    // Trigger animations after mount
    setTimeout(() => setShowContent(true), 100);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    const result = await login(loginData.email, loginData.password);
    
    if (!result.success) {
      setError(result.error);
    }
    setIsLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    
    if (registerData.password !== registerData.confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      return;
    }
    
    if (registerData.password.length < 6) {
      setError('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }
    
    setIsLoading(true);
    const result = await register(registerData.name, registerData.email, registerData.password);
    
    if (!result.success) {
      setError(result.error);
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f1729] via-[#1c2e52] to-[#0f1729] flex overflow-hidden">
      {/* Animated background particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <FloatingParticle delay={0} duration={4} size="100px" left={10} top={20} />
        <FloatingParticle delay={1} duration={5} size="150px" left={80} top={10} />
        <FloatingParticle delay={2} duration={6} size="80px" left={60} top={70} />
        <FloatingParticle delay={0.5} duration={4.5} size="120px" left={20} top={80} />
        <FloatingParticle delay={1.5} duration={5.5} size="90px" left={90} top={50} />
        
        {/* Gradient orbs */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#4b95d1]/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      {/* Left side - Branding & Features (hidden on mobile) */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center p-12 relative">
        <div className={`transform transition-all duration-1000 ${showContent ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'}`}>
          {/* Logo */}
          <div className="flex items-center gap-4 mb-8">
            <div className="relative">
              <div className="w-16 h-16 bg-gradient-to-br from-[#4b95d1] to-[#6366f1] rounded-2xl flex items-center justify-center shadow-2xl shadow-[#4b95d1]/30">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-[#0f1729] animate-pulse" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-white tracking-tight">WAHR</h1>
              <p className="text-[#4b95d1] text-sm font-medium">Privatinstitut</p>
            </div>
          </div>

          {/* Tagline */}
          <h2 className="text-3xl font-bold text-white mb-4 leading-tight">
            Provisionen verwalten.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#4b95d1] to-purple-400">
              Einfach. Übersichtlich.
            </span>
          </h2>
          <p className="text-white/60 mb-8 text-lg">
            Alle Kampagnen, Trainer und Abrechnungen an einem Ort.
          </p>

          {/* Feature cards */}
          <div className="space-y-3">
            <FeatureCard 
              icon={Calendar} 
              title="Zeitstrahl-Übersicht" 
              description="Alle Kalenderwochen auf einen Blick"
              delay={200}
            />
            <FeatureCard 
              icon={TrendingUp} 
              title="Provisionsrechner" 
              description="Automatische Berechnung aller Einheiten"
              delay={400}
            />
            <FeatureCard 
              icon={Users} 
              title="Trainee-Management" 
              description="Einfache Verwaltung aller Trainees"
              delay={600}
            />
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12">
        <div className={`w-full max-w-md transform transition-all duration-1000 ${showContent ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          
          {/* Mobile logo (shown only on mobile) */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#4b95d1] to-[#6366f1] rounded-2xl shadow-2xl shadow-[#4b95d1]/30 mb-4">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white tracking-tight">WAHR</h1>
            <p className="text-[#4b95d1] text-sm">Privatinstitut für Dialog & Markenkompetenz</p>
          </div>

          {/* Login Card */}
          <Card className="bg-white/10 backdrop-blur-xl border-white/20 shadow-2xl overflow-hidden">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-white/5 p-1 m-4 mb-0 rounded-lg">
                <TabsTrigger 
                  value="login" 
                  className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#4b95d1] data-[state=active]:to-[#6366f1] data-[state=active]:text-white data-[state=active]:shadow-lg text-white/60 transition-all duration-300 rounded-md"
                >
                  Anmelden
                </TabsTrigger>
                <TabsTrigger 
                  value="register"
                  className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#4b95d1] data-[state=active]:to-[#6366f1] data-[state=active]:text-white data-[state=active]:shadow-lg text-white/60 transition-all duration-300 rounded-md"
                >
                  Registrieren
                </TabsTrigger>
              </TabsList>

              {/* Login Tab */}
              <TabsContent value="login" className="mt-0">
                <CardHeader className="pb-4">
                  <CardTitle className="text-white text-xl">Willkommen zurück!</CardTitle>
                  <CardDescription className="text-white/60">
                    Melden Sie sich an, um fortzufahren
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleLogin} className="space-y-4">
                    {error && (
                      <Alert variant="destructive" className="bg-red-500/10 border-red-500/30 text-red-200">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    )}
                    
                    <div className="space-y-2">
                      <Label htmlFor="login-email" className="text-white/80 text-sm">E-Mail</Label>
                      <div className="relative group">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-white/40 group-focus-within:text-[#4b95d1] transition-colors" />
                        <Input
                          id="login-email"
                          type="email"
                          placeholder="trainer@wahr.de"
                          value={loginData.email}
                          onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                          className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#4b95d1] focus:ring-[#4b95d1]/20 transition-all"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="login-password" className="text-white/80 text-sm">Passwort</Label>
                      <div className="relative group">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-white/40 group-focus-within:text-[#4b95d1] transition-colors" />
                        <Input
                          id="login-password"
                          type="password"
                          placeholder="••••••••"
                          value={loginData.password}
                          onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                          className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#4b95d1] focus:ring-[#4b95d1]/20 transition-all"
                          required
                        />
                      </div>
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-gradient-to-r from-[#4b95d1] to-[#6366f1] hover:from-[#4b95d1]/90 hover:to-[#6366f1]/90 text-white font-semibold py-5 shadow-lg shadow-[#4b95d1]/25 transition-all duration-300 hover:shadow-xl hover:shadow-[#4b95d1]/30 group"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          Anmelden
                          <ChevronRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </TabsContent>

              {/* Register Tab */}
              <TabsContent value="register" className="mt-0">
                <CardHeader className="pb-4">
                  <CardTitle className="text-white text-xl">Konto erstellen</CardTitle>
                  <CardDescription className="text-white/60">
                    Registrieren Sie sich als neuer Trainer
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleRegister} className="space-y-4">
                    {error && (
                      <Alert variant="destructive" className="bg-red-500/10 border-red-500/30 text-red-200">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    )}
                    
                    <div className="space-y-2">
                      <Label htmlFor="register-name" className="text-white/80 text-sm">Name</Label>
                      <div className="relative group">
                        <User className="absolute left-3 top-3 h-4 w-4 text-white/40 group-focus-within:text-[#4b95d1] transition-colors" />
                        <Input
                          id="register-name"
                          type="text"
                          placeholder="Max Mustermann"
                          value={registerData.name}
                          onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                          className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#4b95d1] focus:ring-[#4b95d1]/20 transition-all"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="register-email" className="text-white/80 text-sm">E-Mail</Label>
                      <div className="relative group">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-white/40 group-focus-within:text-[#4b95d1] transition-colors" />
                        <Input
                          id="register-email"
                          type="email"
                          placeholder="ihre.email@beispiel.de"
                          value={registerData.email}
                          onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                          className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#4b95d1] focus:ring-[#4b95d1]/20 transition-all"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="register-password" className="text-white/80 text-sm">Passwort</Label>
                        <div className="relative group">
                          <Lock className="absolute left-3 top-3 h-4 w-4 text-white/40 group-focus-within:text-[#4b95d1] transition-colors" />
                          <Input
                            id="register-password"
                            type="password"
                            placeholder="••••••"
                            value={registerData.password}
                            onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                            className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#4b95d1] focus:ring-[#4b95d1]/20 transition-all"
                            required
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="register-confirm" className="text-white/80 text-sm">Bestätigen</Label>
                        <div className="relative group">
                          <Lock className="absolute left-3 top-3 h-4 w-4 text-white/40 group-focus-within:text-[#4b95d1] transition-colors" />
                          <Input
                            id="register-confirm"
                            type="password"
                            placeholder="••••••"
                            value={registerData.confirmPassword}
                            onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                            className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#4b95d1] focus:ring-[#4b95d1]/20 transition-all"
                            required
                          />
                        </div>
                      </div>
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-gradient-to-r from-[#4b95d1] to-[#6366f1] hover:from-[#4b95d1]/90 hover:to-[#6366f1]/90 text-white font-semibold py-5 shadow-lg shadow-[#4b95d1]/25 transition-all duration-300 hover:shadow-xl hover:shadow-[#4b95d1]/30 group"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          Registrieren
                          <ChevronRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </TabsContent>
            </Tabs>
          </Card>

          {/* Footer */}
          <p className="text-center text-white/40 text-xs mt-6">
            © 2025 WAHR - Privatinstitut für Dialog & Markenkompetenz
          </p>
        </div>
      </div>

      {/* CSS for custom animations */}
      <style>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out;
        }
      `}</style>
    </div>
  );
};

export default LoginPage;
